import { encounterField } from '../../shared/array-types';
import { ftEncounterShapeOrDerived } from '../port/k2-geometry/ft3d_shape_core';
import { native } from '../port/k2-geometry/php-shapes';
import { geometryEnvironment } from './port-environment';
import { validateShape } from '../../../../ft3d/engine/ft3d-shape-core.js';

type Row = Record<string, unknown>;
const shapes = new WeakMap<Row, { inputs: unknown[]; shape?: Row; error?: unknown; storedNull: boolean }>();
const validated = new WeakMap<Row, Row | null>();
/** ft_encounter_shape_or_derived() over a page-held encounter: the stored shape, else the one-box exterior fallback a never-authored
 *  encounter projects from (its NULL column is absent from the compiled bucket). A malformed stored blob throws. */
export function encounterShapeOrDerived(encounter: Row): Row {
  let raw = encounterField<unknown>(encounter, 'encounter_shape');
  const inputs = raw == null || raw === ''
    ? [raw, ...['encounter_max_x', 'encounter_max_y', 'encounter_ceiling', 'encounter_max_z', 'encounter_interior_type'].map(key => encounterField(encounter, key))]
    : [raw];
  const held = shapes.get(encounter);
  if (held && inputs.length === held.inputs.length && inputs.every((value, i) => value === held.inputs[i])) {
    if ('error' in held) throw held.error;
    return held.shape!;
  }
  if (typeof raw === 'string') {
    try { raw = raw === '' ? null : JSON.parse(raw); }
    catch (error) { shapes.set(encounter, { inputs, error, storedNull: false }); throw error; }
  }
  // The port passes this native page row through unchanged; read its column without copying the roster/catalog.
  const env = geometryEnvironment({ ft_encounter_shape: () => raw ?? null, encounter_field: encounterField });
  const shape = (native(ftEncounterShapeOrDerived(encounter, env as unknown as Parameters<typeof ftEncounterShapeOrDerived>[1])) as { shape: Row }).shape;
  shapes.set(encounter, { inputs, shape, storedNull: typeof inputs[0] === 'string' && inputs[0] !== '' && raw === null });
  return shape;
}

/** Existing read-only consumers treat a malformed stored value (including JSON null) as unknown. */
export function encounterShapeForReaders(encounter: Row): Row | null {
  let shape: Row;
  try { shape = encounterShapeOrDerived(encounter); } catch { return null; }
  return shapes.get(encounter)?.storedNull || shape === null || typeof shape !== 'object' || Array.isArray(shape) ? null : shape;
}

/** Placement readers retain the same malformed-shape result, validating each stored revision once. */
export function validatedEncounterShape(encounter: Row): Row | null {
  const shape = encounterShapeForReaders(encounter);
  if (!shape) return null;
  if (validated.has(shape)) return validated.get(shape)!;
  let result: Row | null = shape;
  try { validateShape(shape); } catch { result = null; }
  validated.set(shape, result);
  return result;
}
